<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import { UiButton, UiDialogShell } from '@peanuds/ui';

import {
  getPendingLivenessDialogState,
  openPendingLivenessDialogLink,
  subscribePendingLivenessDialog,
} from './pending-liveness-dialog';
import { useAuthStore } from '../auth0/store/auth.store';

import pendingLivenessDialogIllustration from '../assets/pending-liveness-dialog-illustration.png';

const PENDING_LIVENESS_DIALOG_TITLE = 'Please confirm your identity';
const PENDING_LIVENESS_DIALOG_MESSAGE =
  'We need to complete a quick security check before you can continue with this action. This usually takes less than a minute.';
const PENDING_LIVENESS_DIALOG_NOTE =
  'This extra verification helps protect your account and confirm that the request is being made by you. Contact support if you have any issues.';

const authStore = useAuthStore();
const dialogState = ref(getPendingLivenessDialogState());

const isVisible = computed(() => dialogState.value.isVisible);
const isConfirmIdentityLoading = computed(() => dialogState.value.launchStatus === 'loading');

let unsubscribe: (() => void) | null = null;

const handleLogout = async () => {
  await authStore.logout();
};

const handleConfirmIdentity = async () => {
  await openPendingLivenessDialogLink();
};

onMounted(() => {
  unsubscribe = subscribePendingLivenessDialog((state) => {
    dialogState.value = state;
  });
});

onUnmounted(() => {
  unsubscribe?.();
});
</script>

<template>
  <UiDialogShell
    :visible="isVisible"
    width="34rem"
    max-width="calc(100vw - 2rem)"
    :show-close="false"
    :dismissable-mask="false"
    :close-on-escape="false"
    :title="PENDING_LIVENESS_DIALOG_TITLE"
    :illustration="pendingLivenessDialogIllustration"
    :pt="{
      mask: {
        class: 'pending-liveness-dialog__mask',
      },
    }"
  >
    <template #body>
      <div class="pending-liveness-dialog__copy">
        <p class="pending-liveness-dialog__message">{{ PENDING_LIVENESS_DIALOG_MESSAGE }}</p>
        <p class="pending-liveness-dialog__message">{{ PENDING_LIVENESS_DIALOG_NOTE }}</p>
      </div>
    </template>

    <template #actions>
      <UiButton label="Logout" severity="secondary" @click="handleLogout" />
      <UiButton
        label="Confirm Identity"
        :loading="isConfirmIdentityLoading"
        @click="handleConfirmIdentity"
      />
    </template>
  </UiDialogShell>
</template>

<style scoped lang="scss">
:global(.pending-liveness-dialog__mask) {
  padding: $space-md;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(4px);
}

.pending-liveness-dialog {
  &__copy {
    display: flex;
    flex-direction: column;
    gap: $spacing-s;
  }

  &__message {
    margin: 0;
    color: $color-text-primary;
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
    line-height: $line-height-xs;
    letter-spacing: -0.056px;
    text-align: center;
  }
}
</style>
